import { type ImgHTMLAttributes, useMemo } from 'react';
import styled from 'styled-components';
import type { IProductImage, ProductImage as ProductImageModel } from 'autogen/swagger/all';
import Image from 'components/generic/Image';
import imageOverlayStyle from 'components/shared/style/imageOverlay';
import type { ImageTransformType } from 'helpers/imageScalingHelper';
import { AspectRatioContainer } from '../Image/Image';
import type { AspectRatioValue } from '../Image/shared/types';

const StyledProductImage = styled(Image.WithScaling)`
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
`;

const StyledAspectRatioContainer = styled(AspectRatioContainer)<{ $maxWidth: string }>`
    width: 100%;
    max-width: ${({ $maxWidth }) => $maxWidth};
`;

export interface IProductImageProps {
    image: ImgHTMLAttributes<HTMLImageElement> & (IProductImage | ProductImageModel); // all image props are spread to the <img/> tag
    width: number;
    transformType: ImageTransformType;
    maxWidth?: string;
    aspectRatio?: AspectRatioValue;
    loading?: HTMLImageElement['loading'];
}

export function ProductImage({
    image,
    width = 136,
    maxWidth = `${width}px`,
    transformType,
    aspectRatio = '1/1',
    loading,
    ...restProps
}: IProductImageProps) {
    const AspectRatioContainerRenderer = useMemo(
        () => (props) => <StyledAspectRatioContainer $maxWidth={maxWidth} {...props} />,
        [maxWidth]
    );

    return (
        <StyledProductImage
            alt={image.alt || ''}
            sizes={width}
            src={image.url}
            transformType={transformType}
            imageType="product"
            loading={loading ?? 'lazy'}
            aspectRatio={aspectRatio}
            AspectRatioContainerRenderer={AspectRatioContainerRenderer}
            objectFit="contain"
            {...restProps}
        />
    );
}

const StyledProductImageWithOverlay = styled(ProductImage)`
    &:after {
        content: '';
        ${imageOverlayStyle}
    }
`;

export function ProductImageWithOverlay(props: IProductImageProps) {
    return <StyledProductImageWithOverlay {...props} />;
}

export default Object.assign(ProductImage, {
    WithOverlay: ProductImageWithOverlay
});
